import { useId } from 'react';

type PendingReviewClipboardWatermarkProps = {
  className?: string;
  /** When true, render at full opacity (default watermark uses 0.2). */
  opaque?: boolean;
  /** Clipboard rotation in degrees (positive = clockwise). Default matches dashboard card. */
  rotateDeg?: number;
  /** Compact centered layout for inline placement beside controls (e.g. title menu). */
  inline?: boolean;
};

/** Decorative clipboard checklist watermark for pending-review dashboard card. */
export const PendingReviewClipboardWatermark = ({
  className = '',
  opaque = false,
  rotateDeg = -19,
  inline = false,
}: PendingReviewClipboardWatermarkProps) => {
  const uid = useId().replace(/:/g, '');
  const lightClipId = `pendingReviewClipboard-light-${uid}`;
  const darkClipId = `pendingReviewClipboard-dark-${uid}`;

  const renderClipboard = (
    clipId: string,
    colors: {
      outerFill: string;
      outerStroke: string;
      paperFill: string;
      clipFill: string;
      clipInnerFill: string;
      holeFill: string;
      accent: string;
    },
  ) => (
    <g id={clipId}>
      <rect
        x="8"
        y="12"
        width="104"
        height="148"
        rx="18"
        ry="18"
        fill={colors.outerFill}
        stroke={colors.outerStroke}
        strokeWidth="12"
      />
      <rect x="18" y="24" width="84" height="128" rx="8" fill={colors.paperFill} />
      <rect x="38" y="8" width="44" height="18" rx="4" fill={colors.clipFill} />
      <rect x="46" y="4" width="28" height="12" rx="3" fill={colors.clipInnerFill} />
      <circle cx="60" cy="10" r="3.5" fill={colors.holeFill} />
      <path
        d="M32 48 L38 56 L50 40"
        fill="none"
        stroke={colors.accent}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M32 72 L38 80 L50 64"
        fill="none"
        stroke={colors.accent}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M32 96 L38 104 L50 88"
        fill="none"
        stroke={colors.accent}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M32 120 L38 128 L50 112"
        fill="none"
        stroke={colors.accent}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="62" y="47" width="32" height="6" rx="1" fill={colors.accent} />
      <rect x="62" y="71" width="32" height="6" rx="1" fill={colors.accent} />
      <rect x="62" y="95" width="32" height="6" rx="1" fill={colors.accent} />
      <rect x="62" y="119" width="32" height="6" rx="1" fill={colors.accent} />
    </g>
  );

  const clipOpacity = opaque ? 1 : 0.2;
  const clipTransform = inline
    ? `translate(4 4) rotate(${rotateDeg} 60 83)`
    : `translate(252 18) rotate(${rotateDeg} 65 80) scale(1.05)`;
  const inlineViewBox = '0 0 128 176';

  return (
    <svg
      className={className}
      width={inline ? 128 : 420}
      height={inline ? 176 : 240}
      viewBox={inline ? inlineViewBox : '0 0 420 240'}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <defs>
        {renderClipboard(lightClipId, {
          outerFill: '#D1FAE5',
          outerStroke: '#A7F3D0',
          paperFill: '#ECFDF5',
          clipFill: '#6EE7B7',
          clipInnerFill: '#94A3B8',
          holeFill: '#CBD5E1',
          accent: '#6EE7B7',
        })}
        {renderClipboard(darkClipId, {
          outerFill: '#134E4A',
          outerStroke: '#2DD4BF',
          paperFill: '#99F6E4',
          clipFill: '#0F766E',
          clipInnerFill: '#64748B',
          holeFill: '#334155',
          accent: '#5EEAD4',
        })}
      </defs>

      <g className="dark:hidden">
        <g transform={clipTransform}>
          <use href={`#${lightClipId}`} opacity={clipOpacity} />
        </g>
      </g>
      <g className="hidden dark:block">
        <g transform={clipTransform}>
          <use href={`#${darkClipId}`} opacity={clipOpacity} />
        </g>
      </g>
    </svg>
  );
};
